// src/pages/Catalog/ProductPage.jsx
//
// Product create/edit page
// ========================
//
// PURPOSE
// -------
// This page owns product creation and product editing.
// Product overview is intentionally separated into ProductOverviewPage.jsx.
//
// Why this split matters:
// - overview will grow with filters, stock indicators, publication state,
//   barcode scanning and website publication status
// - product editing has many structured fields and media upload logic
// - separating them keeps both screens maintainable, similar to the Voucher area
//
// PRODUCT MODEL
// -------------
// Product = one concrete sellable / stock-manageable SKU variant.
//
// Example:
//   Deodorant Blue 100 ml = one product row
//   Deodorant Blue 200 ml = another product row
//
// Important fields:
// - variant_group_key groups variants for website display
// - variant_label is the customer-facing variant option
// - content_quantity + content_unit_code describe what one sellable item contains
// - stock_unit_code defines how future stock is counted
// - price_input + price_entry_mode + VAT calculate net/VAT/gross
//
// GENERIC MEDIA MODEL
// -------------------
// Product images are no longer handled by product-specific endpoints.
//
// Old direction:
//   /catalog/products/:id/images
//
// New direction:
//   /media/product/:productId
//
// Backend stores images in generic media_files:
//
//   owner_type = "product"
//   owner_id   = product.id
//   media_role = "gallery"
//
// The product API still returns product.images as a convenient frontend alias,
// but those rows are MediaFile rows.
//
// IMPORTANT AUTH NOTE
// -------------------
// Protected media should not be rendered through raw:
//
//   <img src="/media/:id/file">
//
// because raw <img> requests do not use apiClient and may miss auth/session
// headers used by hb_visits.
//
// Therefore ProductPage renders media with MediaFileImage, which fetches the
// media file as Blob through apiClient and then renders an object URL.
//
// IMAGE UPLOAD
// ------------
// Users can add images by:
// - drag and drop
// - file picker
//
// Browser security does not allow a web app to read arbitrary local file paths.
// Therefore "selecting a path" is implemented as standard file selection.
//
// In CREATE mode:
// - images are staged locally first
// - after product save, they are uploaded to /media/product/:newProductId
//
// In EDIT mode:
// - selected/dropped images are uploaded immediately to /media/product/:id
//
// UX / LAYOUT DESIGN
// ------------------
// This page uses an internal app-like layout:
//
//   page header
//   scrollable form body
//   fixed/stable bottom action bar inside the page
//
// The bottom action bar is intentionally separated from the scrollable body.
// This prevents Save / Cancel buttons from being clipped at the bottom of the
// dashboard layout.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./ProductPage.css";

import { fetchProductCategories } from "../../services/productCategoriesService";
import { fetchUnitsOfMeasure } from "../../services/unitsOfMeasureService";

import {
  createProduct,
  fetchProductById,
  updateProduct,
} from "../../services/productsService";

import {
  deleteOwnerMedia,
  setPrimaryOwnerMedia,
  uploadOwnerMedia,
} from "../../services/mediaFileService";

import MediaFileImage from "../../components/media/MediaFileImage";

const PRICE_MODE_GROSS = "GROSS_INCLUDES_VAT";
const PRICE_MODE_NET = "NET_PLUS_VAT";

const MEDIA_OWNER_PRODUCT = "product";
const MEDIA_ROLE_GALLERY = "gallery";

function asBool(v) {
  return (
    v === true ||
    String(v).trim().toLowerCase() === "true" ||
    String(v).trim() === "1"
  );
}

function toNumberOrNull(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function money(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(2);
}

function roundMoney(value) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/**
 * Frontend pricing preview.
 *
 * Backend calculation remains authoritative.
 * This preview is only for immediate user feedback.
 */
function calculatePricingPreview(form) {
  const input = toNumberOrNull(form.price_input);
  const vatRate = toNumberOrNull(form.vat_rate_percent) ?? 0;

  if (input == null || input < 0 || vatRate < 0) {
    return { net: null, vat: null, gross: null };
  }

  if (form.price_entry_mode === PRICE_MODE_NET) {
    const net = roundMoney(input);
    const vat = roundMoney(net * (vatRate / 100));
    const gross = roundMoney(net + vat);
    return { net, vat, gross };
  }

  const gross = roundMoney(input);
  const net = vatRate === 0 ? gross : roundMoney(gross / (1 + vatRate / 100));
  const vat = roundMoney(gross - net);

  return { net, vat, gross };
}

function slugify(input) {
  return String(input || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 150);
}

function initialForm() {
  return {
    category_id: "",
    name_public: "",
    brand: "",
    sku: "",
    barcode: "",
    variant_group_key: "",
    variant_label: "",
    packaging_label: "",
    content_quantity: "",
    content_unit_code: "",
    stock_unit_code: "pcs",
    tracks_batches: false,
    requires_batch_on_receipt: false,
    requires_expiry_on_receipt: false,
    default_issue_batch_policy: "none",
    allow_issue_without_batch: true,
    stock_valuation_method: "product_moving_average",
    description_short: "",
    price_entry_mode: PRICE_MODE_GROSS,
    price_input: "",
    vat_rate_percent: "0",
    sort_order: 0,
    is_active: true,
    publish_to_website: true,
  };
}

function mapProductToForm(row) {
  return {
    category_id: row?.category_id ? String(row.category_id) : "",
    name_public: row?.name_public || "",
    brand: row?.brand || "",
    sku: row?.sku || "",
    barcode: row?.barcode || "",
    variant_group_key: row?.variant_group_key || "",
    variant_label: row?.variant_label || "",
    packaging_label: row?.packaging_label || "",
    content_quantity: row?.content_quantity ?? "",
    content_unit_code: row?.content_unit_code || "",
    stock_unit_code: row?.stock_unit_code || "pcs",
    tracks_batches: asBool(row?.tracks_batches),
    requires_batch_on_receipt: asBool(row?.requires_batch_on_receipt),
    requires_expiry_on_receipt: asBool(row?.requires_expiry_on_receipt),
    default_issue_batch_policy: row?.default_issue_batch_policy || "none",
    allow_issue_without_batch: row?.allow_issue_without_batch === undefined ? true : asBool(row?.allow_issue_without_batch),
    stock_valuation_method: row?.stock_valuation_method || "product_moving_average",
    description_short: row?.description_short || "",
    price_entry_mode: row?.price_entry_mode || PRICE_MODE_GROSS,
    price_input: row?.price_input ?? "",
    vat_rate_percent: row?.vat_rate_percent ?? "0",
    sort_order: Number(row?.sort_order || 0),
    is_active: asBool(row?.is_active),
    publish_to_website: asBool(row?.publish_to_website),
  };
}

/**
 * Create backend payload.
 *
 * Important:
 * - backend calculates price_net / price_vat / price_gross again
 * - empty strings are normalized by backend as well
 * - product media are not part of this payload
 */
function createPayload(form) {
  return {
    category_id: Number(form.category_id),
    name_public: form.name_public,
    brand: form.brand,
    sku: form.sku,
    barcode: form.barcode,
    variant_group_key: form.variant_group_key,
    variant_label: form.variant_label,
    packaging_label: form.packaging_label,
    content_quantity:
      form.content_quantity === "" ? null : form.content_quantity,
    content_unit_code: form.content_unit_code || null,
    stock_unit_code: form.stock_unit_code || "pcs",
    tracks_batches: !!form.tracks_batches,
    requires_batch_on_receipt: !!form.requires_batch_on_receipt,
    requires_expiry_on_receipt: !!form.requires_expiry_on_receipt,
    default_issue_batch_policy: form.default_issue_batch_policy || "none",
    allow_issue_without_batch: !!form.allow_issue_without_batch,
    stock_valuation_method: form.stock_valuation_method || "product_moving_average",
    description_short: form.description_short,
    price_entry_mode: form.price_entry_mode,
    price_input: form.price_input === "" ? null : form.price_input,
    vat_rate_percent: form.vat_rate_percent === "" ? 0 : form.vat_rate_percent,
    sort_order: Number(form.sort_order || 0),
    is_active: !!form.is_active,
    publish_to_website: !!form.publish_to_website,
  };
}

function imageList(product) {
  return Array.isArray(product?.images) ? product.images : [];
}

function getPrimaryImage(product) {
  const images = imageList(product);
  if (!images.length) return null;
  return images.find((img) => asBool(img.is_primary)) || images[0];
}

function getUnitSymbol(units, code) {
  if (!code) return "";
  const row = units.find((u) => String(u.code) === String(code));
  return row?.symbol || code;
}

function formatFormContent(form, units) {
  const qty = String(form.content_quantity || "").trim();
  if (!qty) return "—";

  const unit = getUnitSymbol(units, form.content_unit_code);
  return unit ? `${qty} ${unit}` : qty;
}

function formatCategoryName(categories, categoryId) {
  const row = categories.find((cat) => String(cat.id) === String(categoryId));
  return row?.name_public || "—";
}

function fileSizeKb(file) {
  const size = Number(file?.size || 0);
  if (!Number.isFinite(size)) return "";
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  /**
   * Return target for Back / Cancel.
   *
   * ProductOverview passes this through React Router state:
   *
   *   state: { returnTo: "/catalog/products?q=...&page=..." }
   *
   * When ProductPage is opened directly, for example from Command Search or by
   * pasting the URL in the browser, no state may exist. In that case we fall
   * back to the plain Product Overview route.
   */
  const backToOverviewPath = location.state?.returnTo || "/catalog/products";

  const isCreate = !id || id === "new";
  const { t } = useTranslation(["common", "catalog"]);

  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState(initialForm());

  const [pendingFiles, setPendingFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const pricingPreview = useMemo(() => calculatePricingPreview(form), [form]);

  const stockUnits = useMemo(
    () => units.filter((u) => asBool(u.is_stock_unit)),
    [units]
  );

  const contentUnits = useMemo(
    () => units.filter((u) => asBool(u.is_content_unit)),
    [units]
  );

  const primaryImage = useMemo(() => getPrimaryImage(product), [product]);

  /**
   * Local object URLs for staged files in CREATE mode.
   *
   * These previews are not backend media yet.
   */
  const pendingFilePreviews = useMemo(() => {
    return pendingFiles.map((file, index) => ({
      file,
      index,
      url: URL.createObjectURL(file),
    }));
  }, [pendingFiles]);

  useEffect(() => {
    return () => {
      pendingFilePreviews.forEach((item) => URL.revokeObjectURL(item.url));
    };
  }, [pendingFilePreviews]);


  /**
   * Auto-hide success messages.
   *
   * UX reason:
   * - success feedback is useful immediately after save/upload/delete,
   * - but it should not stay permanently on the form,
   * - otherwise the page feels "dirty" even though the operation is finished.
   */
  useEffect(() => {
    if (!success) return undefined;

    const timer = window.setTimeout(() => {
      setSuccess("");
    }, 3500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [success]);

  async function loadDictionaries() {
    const [cats, uom] = await Promise.all([
      fetchProductCategories({ active: true }),
      fetchUnitsOfMeasure({ active: true }),
    ]);

    setCategories(Array.isArray(cats) ? cats : []);
    setUnits(Array.isArray(uom) ? uom : []);
  }

  async function loadProduct(productId) {
    const row = await fetchProductById(productId);
    setProduct(row);
    setForm(mapProductToForm(row || {}));
  }

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      setError("");

      try {
        await loadDictionaries();

        if (!isCreate) {
          const row = await fetchProductById(id);

          if (cancelled) return;

          setProduct(row);
          setForm(mapProductToForm(row || {}));
        } else if (!cancelled) {
          setProduct(null);
          setForm(initialForm());
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || t("catalog:productPage.messages.initializeFailed"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [id, isCreate, t]);

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function autoVariantGroupKey() {
    const base = [form.brand, form.name_public].filter(Boolean).join(" ");
    const value = slugify(base);
    setField("variant_group_key", value);
  }

  function autoVariantLabel() {
    const qty = String(form.content_quantity || "").trim();
    const unit = String(form.content_unit_code || "").trim();

    if (!qty || !unit) return;

    setField("variant_label", `${qty} ${unit}`);
  }

  function addPendingFiles(fileList) {
    const next = Array.from(fileList || []).filter(Boolean);
    if (!next.length) return;

    setPendingFiles((prev) => [...prev, ...next]);
  }

  function removePendingFile(index) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }

  /**
   * Upload files using generic media endpoint:
   *
   *   POST /media/product/:productId
   *
   * The backend stores:
   *   owner_type = product
   *   owner_id   = productId
   */
  async function uploadFilesForProduct(productId, files) {
    const fileList = Array.from(files || []).filter(Boolean);
    if (!fileList.length) return;

    setUploading(true);

    try {
      await uploadOwnerMedia(MEDIA_OWNER_PRODUCT, productId, fileList, {
        media_role: MEDIA_ROLE_GALLERY,
      });

      setPendingFiles([]);

      const refreshed = await fetchProductById(productId);
      setProduct(refreshed);
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = createPayload(form);
      let saved;

      if (isCreate) {
        saved = await createProduct(payload);
        setSuccess(t("catalog:productPage.messages.created"));
      } else {
        saved = await updateProduct(id, payload);
        setSuccess(t("catalog:productPage.messages.updated"));
      }

      const productId = saved?.id || id;

      if (productId && pendingFiles.length > 0) {
        await uploadFilesForProduct(productId, pendingFiles);
      }

      if (isCreate && productId) {
        /**
         * After create we stay on the newly created product in edit mode.
         *
         * Important:
         * Keep returnTo in router state. Otherwise the header/footer Back/Cancel links
         * would lose the original overview state after the create route is replaced by
         * the concrete edit route.
         */
        navigate(`/catalog/products/${productId}`, {
          replace: true,
          state: {
            returnTo: backToOverviewPath,
            phase: "edit",
          },
        });
      } else if (productId) {
        await loadProduct(productId);
      }
    } catch (e2) {
      setError(t(`catalog:productPage.stockIdentityErrors.${e2?.code}`, {
        defaultValue: e2?.message || t("catalog:productPage.messages.saveFailed"),
      }));
    } finally {
      setSaving(false);
    }
  }

  async function handleImmediateUpload(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    if (isCreate) {
      addPendingFiles(files);
      return;
    }

    setError("");
    setSuccess("");

    try {
      await uploadFilesForProduct(id, files);
      setSuccess(t("catalog:productPage.messages.imageUploaded"));
    } catch (e) {
      setError(e?.message || t("catalog:productPage.messages.imageUploadFailed"));
    }
  }

  function handleFilePickerChange(e) {
    handleImmediateUpload(e.target.files);

    /**
     * Clear input value so selecting the same file again still triggers
     * onChange in the browser.
     */
    e.target.value = "";
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();

    setDragActive(false);
    handleImmediateUpload(e.dataTransfer.files);
  }

  async function handleSetPrimary(mediaId) {
    setError("");
    setSuccess("");

    try {
      await setPrimaryOwnerMedia(MEDIA_OWNER_PRODUCT, id, mediaId, {
        media_role: MEDIA_ROLE_GALLERY,
      });

      await loadProduct(id);
      setSuccess(t("catalog:productPage.messages.primaryUpdated"));
    } catch (e) {
      setError(e?.message || t("catalog:productPage.messages.primaryUpdateFailed"));
    }
  }

  async function handleDeleteImage(mediaId) {
    setError("");
    setSuccess("");

    const ok = window.confirm(t("catalog:productPage.confirmDeleteImage"));
    if (!ok) return;

    try {
      await deleteOwnerMedia(MEDIA_OWNER_PRODUCT, id, mediaId, {
        media_role: MEDIA_ROLE_GALLERY,
      });

      await loadProduct(id);
      setSuccess(t("catalog:productPage.messages.imageDeleted"));
    } catch (e) {
      setError(e?.message || t("catalog:productPage.messages.imageDeleteFailed"));
    }
  }

  const pageModeLabel = isCreate ? t("catalog:productPage.mode.create") : t("catalog:productPage.mode.edit");

  const saveLabel =
    saving || uploading
      ? t("catalog:productPage.actions.saving")
      : isCreate
      ? t("catalog:productPage.actions.createProduct")
      : t("catalog:productPage.actions.saveChanges");

  return (
    <div className="pc-page">
      <div className="pc-page__inner pc-page__inner--narrow">
        <header className="pc-header">
          <div className="pc-header__copy">
            <div className="pc-kicker">
              <span>{pageModeLabel}</span>

              <span
                className={form.is_active ? "pc-badge pc-badge--green" : "pc-badge"}
              >
                {form.is_active ? t("common:status.active") : t("common:status.inactive")}
              </span>

              {form.publish_to_website ? (
                <span className="pc-badge pc-badge--blue">{t("catalog:productPage.badges.website")}</span>
              ) : (
                <span className="pc-badge">{t("catalog:productPage.badges.notPublished")}</span>
              )}
            </div>

            <h1>{isCreate ? t("catalog:productPage.title.add") : t("catalog:productPage.title.edit")}</h1>

            <p>
              {t("catalog:productPage.subtitle")}
            </p>
          </div>

          <div className="pc-header__actions">
            <Link className="pc-btn pc-btn--soft" to={backToOverviewPath}>
              {t("catalog:productPage.actions.backToOverview")}
            </Link>
          </div>
        </header>

        {loading ? (
          <section className="pc-loadingCard">
            <div className="pc-loaderDot" />
            <div>
              <strong>{t("catalog:productPage.loading.title")}</strong>
              <p>{t("catalog:productPage.loading.description")}</p>
            </div>
          </section>
        ) : (
          <form className="pc-formShell" onSubmit={handleSubmit}>
            <div className="pc-formBody">
              {error ? (
                <div className="pc-alert pc-alert--error">{error}</div>
              ) : null}

              {success ? (
                <div className="pc-alert pc-alert--success">{success}</div>
              ) : null}

              <div className="pc-contentGrid">
                <main className="pc-mainColumn">
                  <section className="pc-section pc-section--hero">
                    <div className="pc-section__title">
                      <span className="pc-sectionIcon">01</span>
                      <div>
                        <h2>{t("catalog:productPage.sections.basic.title")}</h2>
                        <p>
                          {t("catalog:productPage.sections.basic.description")}
                        </p>
                      </div>
                    </div>

                    <div className="pc-grid">
                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.categoryRequired")}</label>
                        <select
                          value={form.category_id}
                          onChange={(e) =>
                            setField("category_id", e.target.value)
                          }
                          required
                        >
                          <option value="">{t("catalog:productPage.options.selectCategory")}</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={String(cat.id)}>
                              {cat.name_public}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.productNameRequired")}</label>
                        <input
                          value={form.name_public}
                          onChange={(e) =>
                            setField("name_public", e.target.value)
                          }
                          placeholder={t("catalog:productPage.placeholders.productName")}
                          required
                        />
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.brand")}</label>
                        <input
                          value={form.brand}
                          onChange={(e) => setField("brand", e.target.value)}
                          placeholder={t("catalog:productPage.placeholders.brand")}
                        />
                      </div>

                      <div className="pc-field pc-field--full">
                        <label>{t("catalog:productPage.fields.shortDescription")}</label>
                        <textarea
                          rows="3"
                          value={form.description_short}
                          onChange={(e) =>
                            setField("description_short", e.target.value)
                          }
                          placeholder={t("catalog:productPage.placeholders.shortDescription")}
                        />
                      </div>
                    </div>
                  </section>

                  <section className="pc-section">
                    <div className="pc-section__title">
                      <span className="pc-sectionIcon">02</span>
                      <div>
                        <h2>{t("catalog:productPage.sections.variant.title")}</h2>
                        <p>
                          {t("catalog:productPage.sections.variant.description")}
                        </p>
                      </div>
                    </div>

                    <div className="pc-grid">
                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.variantGroupKey")}</label>

                        <div className="pc-inputWithButton">
                          <input
                            value={form.variant_group_key}
                            placeholder={t("catalog:productPage.placeholders.variantGroupKey")}
                            onChange={(e) =>
                              setField("variant_group_key", e.target.value)
                            }
                          />

                          <button
                            type="button"
                            className="pc-btn pc-btn--small"
                            onClick={autoVariantGroupKey}
                          >
                            {t("catalog:productPage.actions.auto")}
                          </button>
                        </div>

                        <small>
                          {t("catalog:productPage.hints.variantGroupKey")}
                        </small>
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.variantLabel")}</label>

                        <div className="pc-inputWithButton">
                          <input
                            value={form.variant_label}
                            placeholder={t("catalog:productPage.placeholders.variantLabel")}
                            onChange={(e) =>
                              setField("variant_label", e.target.value)
                            }
                          />

                          <button
                            type="button"
                            className="pc-btn pc-btn--small"
                            onClick={autoVariantLabel}
                          >
                            {t("catalog:productPage.actions.auto")}
                          </button>
                        </div>

                        <small>{t("catalog:productPage.hints.variantLabel")}</small>
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.packagingLabel")}</label>
                        <input
                          value={form.packaging_label}
                          placeholder={t("catalog:productPage.placeholders.packagingLabel")}
                          onChange={(e) =>
                            setField("packaging_label", e.target.value)
                          }
                        />
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.contentQuantity")}</label>
                        <input
                          type="number"
                          step="0.001"
                          value={form.content_quantity}
                          onChange={(e) =>
                            setField("content_quantity", e.target.value)
                          }
                          placeholder={t("catalog:productPage.placeholders.contentQuantity")}
                        />
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.contentUnit")}</label>
                        <select
                          value={form.content_unit_code}
                          onChange={(e) =>
                            setField("content_unit_code", e.target.value)
                          }
                        >
                          <option value="">{t("catalog:productPage.options.noContentUnit")}</option>
                          {contentUnits.map((u) => (
                            <option key={u.code} value={u.code}>
                              {u.name_public} ({u.symbol})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </section>

                  <section className="pc-section">
                    <div className="pc-section__title">
                      <span className="pc-sectionIcon">03</span>
                      <div>
                        <h2>{t("catalog:productPage.sections.inventory.title")}</h2>
                        <p>
                          {t("catalog:productPage.sections.inventory.description")}
                        </p>
                      </div>
                    </div>

                    <div className="pc-grid">
                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.sku")}</label>
                        <input
                          value={form.sku}
                          onChange={(e) => setField("sku", e.target.value)}
                          placeholder={t("catalog:productPage.placeholders.sku")}
                        />
                        <small>{t("catalog:productPage.hints.sku")}</small>
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.barcode")}</label>
                        <input
                          value={form.barcode}
                          placeholder={t("catalog:productPage.placeholders.barcode")}
                          onChange={(e) => setField("barcode", e.target.value)}
                        />
                        <small>{t("catalog:productPage.hints.barcode")}</small>
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.stockUnitRequired")}</label>
                        <select
                          value={form.stock_unit_code}
                          onChange={(e) =>
                            setField("stock_unit_code", e.target.value)
                          }
                          required
                        >
                          {stockUnits.length === 0 ? (
                            <option value="pcs">pcs</option>
                          ) : (
                            stockUnits.map((u) => (
                              <option key={u.code} value={u.code}>
                                {u.name_public} ({u.symbol})
                              </option>
                            ))
                          )}
                        </select>
                        <small>{t("catalog:productPage.hints.stockUnit")}</small>
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.stockValuationMethod")}</label>
                        <select
                          value={form.stock_valuation_method}
                          onChange={(e) => {
                            const value = e.target.value;
                            setField("stock_valuation_method", value);
                            if (value === "batch_moving_average") {
                              setField("tracks_batches", true);
                              setField("requires_batch_on_receipt", true);
                              setField("allow_issue_without_batch", false);
                              if (form.default_issue_batch_policy === "none") {
                                setField("default_issue_batch_policy", "FEFO");
                              }
                            }
                          }}
                        >
                          <option value="product_moving_average">
                            {t("catalog:productPage.options.valuationProductMovingAverage")}
                          </option>
                          <option value="batch_moving_average">
                            {t("catalog:productPage.options.valuationBatchMovingAverage")}
                          </option>
                        </select>
                        <small>{t("catalog:productPage.hints.stockValuationMethod")}</small>
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.defaultIssueBatchPolicy")}</label>
                        <select
                          value={form.default_issue_batch_policy}
                          onChange={(e) =>
                            setField("default_issue_batch_policy", e.target.value)
                          }
                        >
                          <option value="none" disabled={form.stock_valuation_method === "batch_moving_average"}>
                            {t("catalog:productPage.options.batchPolicyNone")}
                          </option>
                          <option value="FEFO">{t("catalog:productPage.options.batchPolicyFefo")}</option>
                          <option value="MANUAL">{t("catalog:productPage.options.batchPolicyManual")}</option>
                        </select>
                        <small>{t("catalog:productPage.hints.defaultIssueBatchPolicy")}</small>
                      </div>

                      <div className="pc-field pc-field--wide">
                        <div className="pc-checks pc-checks--stacked">
                          <label>
                            <input
                              type="checkbox"
                              checked={!!form.tracks_batches}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setField("tracks_batches", checked);
                                if (!checked) {
                                  setField("requires_batch_on_receipt", false);
                                  setField("requires_expiry_on_receipt", false);
                                  setField("default_issue_batch_policy", "none");
                                  setField("allow_issue_without_batch", true);
                                  setField("stock_valuation_method", "product_moving_average");
                                }
                              }}
                            />
                            <span>{t("catalog:productPage.fields.tracksBatches")}</span>
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={!!form.requires_batch_on_receipt}
                              disabled={!form.tracks_batches || form.stock_valuation_method === "batch_moving_average"}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setField("requires_batch_on_receipt", checked);
                                if (!checked) setField("requires_expiry_on_receipt", false);
                              }}
                            />
                            <span>{t("catalog:productPage.fields.requiresBatchOnReceipt")}</span>
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={!!form.requires_expiry_on_receipt}
                              disabled={!form.tracks_batches || !form.requires_batch_on_receipt}
                              onChange={(e) => setField("requires_expiry_on_receipt", e.target.checked)}
                            />
                            <span>{t("catalog:productPage.fields.requiresExpiryOnReceipt")}</span>
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={!!form.allow_issue_without_batch}
                              disabled={!form.tracks_batches || form.stock_valuation_method === "batch_moving_average"}
                              onChange={(e) => setField("allow_issue_without_batch", e.target.checked)}
                            />
                            <span>{t("catalog:productPage.fields.allowIssueWithoutBatch")}</span>
                          </label>
                        </div>
                        <small>{t("catalog:productPage.hints.stockTrackingPolicy")}</small>
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.sortOrder")}</label>
                        <input
                          type="number"
                          value={form.sort_order}
                          onChange={(e) => setField("sort_order", e.target.value)}
                        />
                      </div>
                    </div>
                  </section>

                  <section className="pc-section">
                    <div className="pc-section__title">
                      <span className="pc-sectionIcon">04</span>
                      <div>
                        <h2>{t("catalog:productPage.sections.pricing.title")}</h2>
                        <p>
                          {t("catalog:productPage.sections.pricing.description")}
                        </p>
                      </div>
                    </div>

                    <div className="pc-grid">
                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.priceEntryMode")}</label>
                        <select
                          value={form.price_entry_mode}
                          onChange={(e) =>
                            setField("price_entry_mode", e.target.value)
                          }
                        >
                          <option value={PRICE_MODE_GROSS}>
                            {t("catalog:productPage.options.grossIncludesVat")}
                          </option>
                          <option value={PRICE_MODE_NET}>{t("catalog:productPage.options.netPlusVat")}</option>
                        </select>
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.priceInput")}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.price_input}
                          onChange={(e) => setField("price_input", e.target.value)}
                          placeholder="0.00"
                        />
                      </div>

                      <div className="pc-field">
                        <label>{t("catalog:productPage.fields.vatRate")}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={form.vat_rate_percent}
                          onChange={(e) =>
                            setField("vat_rate_percent", e.target.value)
                          }
                        />
                      </div>

                      <div className="pc-pricePreview pc-field--full">
                        <div>
                          <span>{t("catalog:productPage.pricePreview.net")}</span>
                          <strong>{money(pricingPreview.net)}</strong>
                        </div>

                        <div>
                          <span>{t("catalog:productPage.pricePreview.vat")}</span>
                          <strong>{money(pricingPreview.vat)}</strong>
                        </div>

                        <div className="pc-pricePreview__main">
                          <span>{t("catalog:productPage.pricePreview.gross")}</span>
                          <strong>{money(pricingPreview.gross)}</strong>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="pc-section pc-section--media">
                    <div className="pc-section__title">
                      <span className="pc-sectionIcon">05</span>
                      <div>
                        <h2>{t("catalog:productPage.sections.images.title")}</h2>
                        <p>
                          Add images by drag and drop or by selecting files. Images
                          are stored as generic media and can later be used by website
                          publication.
                        </p>
                      </div>
                    </div>

                    <div
                      className={`pc-dropzone ${
                        dragActive ? "pc-dropzone--active" : ""
                      }`}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                      }}
                      onDrop={handleDrop}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        hidden
                        onChange={handleFilePickerChange}
                      />

                      <div className="pc-dropzone__icon" aria-hidden="true">
                        +
                      </div>

                      <p>{t("catalog:productPage.images.dragHere")}</p>
                      <span>{t("catalog:productPage.images.or")}</span>

                      <button
                        type="button"
                        className="pc-btn pc-btn--soft"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        {t("catalog:productPage.actions.selectImages")}
                      </button>

                      <small>{t("catalog:productPage.images.supportedFormats")}</small>
                    </div>

                    {pendingFiles.length > 0 ? (
                      <div className="pc-pendingFiles">
                        <div className="pc-pendingFiles__header">
                          <strong>{t("catalog:productPage.images.waitingForSave")}</strong>
                          <span>{t("catalog:productPage.images.fileCount", { count: pendingFiles.length })}</span>
                        </div>

                        <div className="pc-pendingGrid">
                          {pendingFilePreviews.map((item) => (
                            <div
                              key={`${item.file.name}-${item.index}`}
                              className="pc-pendingFile"
                            >
                              <img src={item.url} alt={item.file.name} />

                              <div>
                                <span>{item.file.name}</span>
                                <small>{fileSizeKb(item.file)}</small>
                              </div>

                              <button
                                type="button"
                                className="pc-btn pc-btn--small"
                                onClick={() => removePendingFile(item.index)}
                              >
                                {t("catalog:productPage.actions.remove")}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {!isCreate && imageList(product).length > 0 ? (
                      <div className="pc-gallery">
                        {imageList(product).map((img) => (
                          <div key={img.id} className="pc-galleryItem">
                            <MediaFileImage
                              media={img}
                              alt={
                                img.original_name ||
                                product?.name_public ||
                                t("catalog:productPage.media.productAlt")
                              }
                              className="pc-galleryImage"
                              fallbackClassName="pc-galleryImageFallback"
                              emptyText={t("catalog:productPage.media.noImage")}
                              loadingText={t("common:common.loading")}
                              failedText={t("catalog:productPage.media.imageUnavailable")}
                            />

                            <div className="pc-galleryItem__meta">
                              <span>
                                {asBool(img.is_primary) ? t("catalog:productPage.media.primary") : t("catalog:productPage.media.image")}
                              </span>
                              <small>{img.original_name || img.file_name}</small>
                            </div>

                            <div className="pc-galleryItem__actions">
                              {!asBool(img.is_primary) ? (
                                <button
                                  type="button"
                                  className="pc-btn pc-btn--small"
                                  onClick={() => handleSetPrimary(img.id)}
                                >
                                  {t("catalog:productPage.actions.setPrimary")}
                                </button>
                              ) : null}

                              <button
                                type="button"
                                className="pc-btn pc-btn--small pc-btn--dangerSoft"
                                onClick={() => handleDeleteImage(img.id)}
                              >
                                {t("common:actions.delete")}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </section>

                  <section className="pc-section pc-section--compact">
                    <div className="pc-checks">
                      <label>
                        <input
                          type="checkbox"
                          checked={!!form.is_active}
                          onChange={(e) =>
                            setField("is_active", e.target.checked)
                          }
                        />
                        <span>{t("common:status.active")}</span>
                      </label>

                      <label>
                        <input
                          type="checkbox"
                          checked={!!form.publish_to_website}
                          onChange={(e) =>
                            setField("publish_to_website", e.target.checked)
                          }
                        />
                        <span>{t("catalog:productPage.fields.publishToWebsite")}</span>
                      </label>
                    </div>
                  </section>
                </main>

                <aside className="pc-sidePanel" aria-label={t("catalog:productPage.summary.aria")}>
                  <div className="pc-summaryCard">
                    <div className="pc-summaryImage">
                      {primaryImage ? (
                        <MediaFileImage
                          media={primaryImage}
                          alt={product?.name_public || t("catalog:productPage.summary.primaryProductAlt")}
                          className="pc-summaryImage__img"
                          fallbackClassName="pc-summaryImage__empty"
                          emptyText={t("catalog:productPage.media.noPrimaryImage")}
                          loadingText={t("common:common.loading")}
                          failedText={t("catalog:productPage.media.imageUnavailable")}
                        />
                      ) : pendingFilePreviews[0]?.url ? (
                        <img
                          src={pendingFilePreviews[0].url}
                          alt={pendingFilePreviews[0].file.name}
                        />
                      ) : (
                        <div className="pc-summaryImage__empty">
                          {t("catalog:productPage.media.noPrimaryImage")}
                        </div>
                      )}
                    </div>

                    <div className="pc-summaryContent">
                      <span className="pc-summaryEyebrow">{t("catalog:productPage.summary.title")}</span>

                      <h3>{form.name_public || t("catalog:productPage.summary.unnamedProduct")}</h3>

                      {form.brand ? <p>{form.brand}</p> : null}

                      <div className="pc-summaryRows">
                        <div>
                          <span>{t("catalog:productPage.summary.category")}</span>
                          <strong>
                            {formatCategoryName(categories, form.category_id)}
                          </strong>
                        </div>

                        <div>
                          <span>{t("catalog:productPage.summary.variant")}</span>
                          <strong>{form.variant_label || "—"}</strong>
                        </div>

                        <div>
                          <span>{t("catalog:productPage.summary.content")}</span>
                          <strong>{formatFormContent(form, units)}</strong>
                        </div>

                        <div>
                          <span>{t("catalog:productPage.summary.stockUnit")}</span>
                          <strong>
                            {getUnitSymbol(units, form.stock_unit_code) || "—"}
                          </strong>
                        </div>

                        <div>
                          <span>{t("catalog:productPage.summary.batchTracking")}</span>
                          <strong>
                            {form.tracks_batches ? t("common:yes", { defaultValue: "Yes" }) : t("common:no", { defaultValue: "No" })}
                          </strong>
                        </div>
                        <div>
                          <span>{t("catalog:productPage.summary.stockValuation")}</span>
                          <strong>{t(`catalog:productPage.options.${form.stock_valuation_method === "batch_moving_average" ? "valuationBatchMovingAverage" : "valuationProductMovingAverage"}`)}</strong>
                        </div>

                        <div>
                          <span>{t("catalog:productPage.summary.grossPrice")}</span>
                          <strong>{money(pricingPreview.gross)}</strong>
                        </div>
                      </div>

                      <div className="pc-summaryBadges">
                        <span
                          className={
                            form.is_active
                              ? "pc-badge pc-badge--green"
                              : "pc-badge"
                          }
                        >
                          {form.is_active ? t("common:status.active") : t("common:status.inactive")}
                        </span>

                        <span
                          className={
                            form.publish_to_website
                              ? "pc-badge pc-badge--blue"
                              : "pc-badge"
                          }
                        >
                          {form.publish_to_website
                            ? t("catalog:productPage.fields.publishToWebsite")
                            : t("catalog:productPage.summary.hiddenFromWebsite")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="pc-helpCard">
                    <h3>{t("catalog:productPage.tips.title")}</h3>
                    <ul>
                      <li>{t("catalog:productPage.tips.sku")}</li>
                      <li>{t("catalog:productPage.tips.barcode")}</li>
                      <li>{t("catalog:productPage.tips.variantGroup")}</li>
                      <li>
                        {t("catalog:productPage.tips.stockUnit")}
                      </li>
                    </ul>
                  </div>
                </aside>
              </div>
            </div>

            <footer className="pc-formFooter">
              <div className="pc-formFooter__meta">
                {uploading
                  ? t("catalog:productPage.footer.uploadingImages")
                  : saving
                  ? t("catalog:productPage.footer.savingProduct")
                  : t("catalog:productPage.footer.ready")}
              </div>

              <div className="pc-formFooter__actions">
                <Link className="pc-btn pc-btn--soft" to={backToOverviewPath}>
                  {t("common:actions.cancel")}
                </Link>

                <button
                  type="submit"
                  className="pc-btn pc-btn--primary pc-btn--save"
                  disabled={saving || uploading}
                >
                  {saveLabel}
                </button>
              </div>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
}
